import {
  Box,
  BoxProps,
  Grid,
  IconButton,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { DuplicateIcon } from 'components/SVG';
import ThumbsDownFilledIcon from 'components/SVG/ThumbsDownFilledIcon';
import ThumbsDownIcon from 'components/SVG/ThumbsDownIcon';
import ThumbsUpFillIcon from 'components/SVG/ThumbsUpFillIcon';
import ThumbsUpIcon from 'components/SVG/ThumbsUpIcon';
import { useChatMessageRatingTypes } from 'hooks/EntityTypes/useChatMessageRatingTypes';
import React from 'react';
import { InfoCard, SparkButton, SparkMarkdown } from 'sharedComponents/UI';
import CopyToClipboardWrapper from '../CopyToClipboardWrapper';
import { BreathingSparkAnimation } from 'components/Animations';
import { useBoardNodesContext } from 'hooks/common';
import { FunctionCall } from 'types/Chats/ChatMessage';
import PluginsPill from './PluginsPill';
import PluginsResultsPill from './PluginsResultsPill';
import BoardWorkingPill from './BoardWorkingPill';

export type FooterActions = 'duplicate' | 'like' | 'dislike';

type Props = {
  id: string;
  message: string;
  isUserMessage: boolean;
  isStreaming: boolean;
  isLastMessage: boolean;
  error: string | null;
  chatMessageRatingTypeID?: number | null | undefined;
  onRetry?: () => void;
  onActionClick?: (
    type: FooterActions,
    messageID: string,
    secondaryID?: string
  ) => void; // secondaryID to show which item was clicked
  infoCardStyles?: SxProps<Theme> | undefined;
  containerStyles?: BoxProps['sx'] | undefined;
  layout?: 'one-column' | 'two-column';
  authorRoleID?: number | null | undefined;
  isExpandedViewOpen?: boolean;
  PluginsUsed?: FunctionCall[];
  handlePluginClicked?: (plugin: FunctionCall) => void;
  pluginsPill?: import('types/Chats/ChatMessage').PluginsPillState;
  boardWorkingPill?: { isVisible: boolean };
};

function ChatMessageBubble({
  id: messageID,
  message,
  chatMessageRatingTypeID,
  isUserMessage,
  isStreaming = false,
  isLastMessage = false,
  error,
  onActionClick,
  infoCardStyles = {},
  containerStyles = {},
  layout = 'two-column',
  authorRoleID,
  onRetry,
  isExpandedViewOpen = false,
  PluginsUsed,
  handlePluginClicked,
  pluginsPill,
  boardWorkingPill,
}: Props) {
  const messageRef = React.useRef<HTMLDivElement | null>(null);
  const { chatMessageRatingTypes, isLoading: isLoadingChatMessageRatingTypes } =
    useChatMessageRatingTypes();
  const { isBoardNodeCollapsedView } = useBoardNodesContext();

  React.useEffect(() => {
    // Only scroll for the last message
    if (!isLastMessage || !messageRef.current) return;

    // For streaming AI messages, keep scrolling as content is added
    if (!isUserMessage && isStreaming) {
      messageRef.current.scrollIntoView({ block: 'end' });
    }
  }, [isLastMessage, isStreaming, message, isUserMessage]);

  const isOneColumn = layout === 'one-column';
  if (!isStreaming && (error || !message)) {
    return (
      <InfoCard
        info='Something went wrong. Please contact our help center'
        type='error'
        cardStyles={{
          width: isOneColumn || isBoardNodeCollapsedView ? '100%' : '80%',
          minWidth: '20rem',
          marginY: 1,
          ...infoCardStyles,
        }}
        infoStyles={{ alignItems: 'center' }}
        iconSize='medium'
      >
        {/* <SparkButton
          variant='contained'
          startIcon={<HistoryIcon color='white' />}
          onClick={() => {
            onRetry && onRetry();
            }}
            >
            retry
            </SparkButton> */}
      </InfoCard>
    );
  }
  const isLoading =
    (isLoadingChatMessageRatingTypes || !message) && !isUserMessage; // only shimmer for AI messages
  
  // Hide the generic loading message when we have plugins pill or board working pill to show
  const shouldShowGenericLoading = isLoading && !pluginsPill?.isVisible && !boardWorkingPill?.isVisible;

  return (
    <Grid
      item
      xs={12}
      className='chat-message-bubble'
      justifyItems={
        isOneColumn ? 'center' : isUserMessage ? 'flex-end' : 'flex-start'
      }
      justifyContent={authorRoleID === 1 ? 'flex-end' : 'flex-start'}
      ref={messageRef}
      data-user-message={isUserMessage ? 'true' : 'false'}
      sx={{
        display: 'flex',
        marginBottom: isLastMessage ? '12px' : '8px',
        marginTop: isLastMessage ? '12px' : '8px',
      }}
    >
      <Box
        maxWidth={
          isOneColumn || isBoardNodeCollapsedView || isLoading ? '100%' : '80%'
        }
        minWidth={'12rem'}
        width={
          authorRoleID === 1 && !isBoardNodeCollapsedView
            ? 'fit-content'
            : '100%'
        }
      >
        <Box
          bgcolor={
            isUserMessage && (isOneColumn || authorRoleID === 1)
              ? 'var( --color-primary-light)'
              : 'white'
          }
          borderRadius='10px'
          overflow='hidden'
          width='100%'
          sx={containerStyles}
        >
          {shouldShowGenericLoading && (
            <Box
              paddingX={2}
              display='flex'
              alignItems='center'
              gap={1}
              paddingY={isExpandedViewOpen ? 1 : 0}
            >
              <Box>
                <BreathingSparkAnimation height={40} width={40} />
              </Box>
              <Typography>Just a sec...</Typography>
            </Box>
          )}
          {!isUserMessage && (pluginsPill || boardWorkingPill) && (
            <Box px={2} pb={isLoading ? 0 : 0.5} pt={isLoading ? 1 : 0}>
              {pluginsPill && <PluginsPill pluginsPill={pluginsPill} />}
              {boardWorkingPill && <BoardWorkingPill isVisible={boardWorkingPill.isVisible} />}
            </Box>
          )}
          {!isLoading && (
            <>
              {message && (
                <Box
                  padding={2}
                  sx={{
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                  }}
                >
                  <SparkMarkdown>{message}</SparkMarkdown>
                </Box>
              )}
              {PluginsUsed && PluginsUsed?.length > 0 && (
                <Box px={3} py={1}>
                  {PluginsUsed?.map((plugin, index) => (
                    <SparkButton
                      variant='outlined'
                      onClick={() => handlePluginClicked?.(plugin)}
                    >
                      {plugin.pluginName}
                    </SparkButton>
                  ))}
                </Box>
              )}
              {!isUserMessage && !isStreaming && (
                <Box display='flex' padding={1}>
                  <CopyToClipboardWrapper textToCopy={message}>
                    <IconButton onClick={() => {}} sx={{ padding: '5px' }}>
                      <DuplicateIcon color='var(--color-text-secondary, rgba(0, 0, 0, 0.6))' width={13} height={13} />
                    </IconButton>
                  </CopyToClipboardWrapper>
                  {chatMessageRatingTypes?.map(({ id, attributes }) => (
                    <IconButton
                      key={`chatMessageRatingTypes-${id}`}
                      sx={{ padding: '5px' }}
                      onClick={() => {
                        console.log(
                          'onActionClick thumbs ',
                          'like',
                          messageID,
                          id
                        );
                        onActionClick && onActionClick('like', messageID, id);
                      }}
                    >
                      {attributes.key === 'like' && (
                        <>
                          {chatMessageRatingTypeID === Number(id) ? (
                            <ThumbsUpFillIcon width='13' height='13' />
                          ) : (
                            <ThumbsUpIcon
                              color='var(--color-text-secondary, rgba(0, 0, 0, 0.6))'
                              width='13'
                              height='13'
                            />
                          )}
                        </>
                      )}
                      {attributes.key === 'dislike' && (
                        <>
                          {chatMessageRatingTypeID === Number(id) ? (
                            <ThumbsDownFilledIcon width='13' height='13' />
                          ) : (
                            <ThumbsDownIcon
                              color='var(--color-text-secondary, rgba(0, 0, 0, 0.6))'
                              width='13'
                              height='13'
                            />
                          )}
                        </>
                      )}
                    </IconButton>
                  ))}
                  {PluginsUsed && PluginsUsed.length > 0 && (
                    <PluginsResultsPill functionCalls={PluginsUsed} />
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Grid>
  );
}

export default ChatMessageBubble;
